<template>
  <modal modal-class="free-upgrade-modal" :anchor="anchors" :hide-header="true" :hide-footer="true" @hidden="clearRoute">
    <div class="d-flex justify-content-center align-items-center flex-column" data-testid="free-upgrade-modal">
      <div class="d-flex justify-content-center align-items-center">
        <span class="masthead">
          {{ layout.heading }}
        </span>
        <pro-badge
          v-if="layout.proBadge"
          utm_campaign="free-upgrade-prompt"
          utm_medium="web"
          :utm_source="page"
          :trial="true"
          :visible="true"
          :learnmore_url="layout.checkoutLink"
        ></pro-badge>
      </div>

      <span v-html="layout.byline" class="byline"></span>

      <div v-if="typeof layout.items[0] == 'string'">
        <ul>
          <li v-for="item in layout.items" :key="item">
            {{ item }}
          </li>
        </ul>
      </div>
      <div v-else class="d-flex justify-content-between">
        <div v-for="item in layout.items" :key="item.text" class="d-flex align-items-center flex-column">
          <img :src="item.image" class="illustration" />
          <span class="item-text">{{ item.text }}</span>
        </div>
      </div>

      <a
        :href="layout.checkoutLink"
        title="Upgrade Plan"
        class="btn button-frame d-flex align-self-stretch align-items-center justify-content-center"
        data-testid="pro-btn-free-upgrade-modal"
      >
        <span class="label">
          {{ layout.label }}
        </span>
      </a>

      <a v-show="!promptDiscountSettings" :href="layout.secondaryCtaLink" class="pricing-link">View all plans</a>
    </div>
  </modal>
</template>

<script>
import AnalyticsImage from "@img/analytics.svg";
import InfiniteImage from "@img/infinite.svg";
import BrandingImage from "@img/branding.svg";
import ProBadge from "@/src/components/pro_badge.vue";
import Modal from "@/src/components/modal.vue";
import track from "@/src/tracking";
import { mapState } from "vuex";

export default {
  components: { Modal, ProBadge },
  data: function () {
    const page = this.$route.path.includes("dashboard") ? "dashboard" : "viewer";
    const brandingAnchor = "#prompt-branding";
    const upgradeAnchor = "#prompt-upgrade";
    const upgradeDiscountAnchor = "#prompt-upgrade-discount";
    const trialAnchor = "#prompt-trial";
    const hardLimitAnchor = "#hard-limit";
    const softLimitAnchor = "#soft-limit";
    const nonCCTrialExpiredAnchor = "#trial-expired";
    const secondaryCtaLink = this.$root.routes.pricing_path();
    const heading = "Get more with";
    const items = [
      { image: InfiniteImage, text: "Unlimited items" },
      { image: AnalyticsImage, text: "Advanced Analytics" },
      { image: BrandingImage, text: "Custom Branding" },
    ];
    return {
      page: page,
      anchors: [trialAnchor, upgradeAnchor, brandingAnchor, hardLimitAnchor, nonCCTrialExpiredAnchor, softLimitAnchor, upgradeDiscountAnchor],
      content: {
        trial: {
          byline: "Try pro <span class='orange-text'>free for 7 days</span> and get great features like:",
          label: "Start free trial",
          items: items,
          checkoutLink: `/purchase?plan=pro&trial=true&onboarding=false&utm_campaign=in_product_prompt_${page}`,
          secondaryCtaLink: secondaryCtaLink,
          anchor: trialAnchor,
          heading: heading,
          proBadge: true,
        },
        upgrade: {
          byline: "Upgrade to a Pro account and unlock features like:",
          label: "Upgrade",
          items: items,
          checkoutLink: `/purchase?plan=pro&trial=true&onboarding=false&utm_campaign=in_product_prompt_${page}`,
          secondaryCtaLink: secondaryCtaLink,
          anchor: upgradeAnchor,
          heading: heading,
          proBadge: true,
        },
        upgradeDiscount: {
          byline: "Upgrade to a Pro account and unlock features like:",
          label: `Claim %${this.promptDiscountSettings?.discount} discount`,
          items: items,
          checkoutLink: this.promptDiscountSettings?.link,
          anchor: upgradeDiscountAnchor,
          heading: `Get %${this.promptDiscountSettings?.discount} off`,
          proBadge: true,
        },
        branding: {
          byline: "Upgrade to a Pro account and unlock custom branding",
          label: "Upgrade",
          items: [
            "Add your own logo to the viewer",
            "Customize your share and redirect urls",
            "Enable/Disable Comments",
            "Control who can view your content",
          ],
          checkoutLink: `/plans?utm_campaign=upgrade&utm_source=${page}_modal&utm_medium=customize`,
          heading: heading,
          proBadge: true,
          anchor: brandingAnchor
        },
        hardLimit: {
          byline: "You’ve hit your item limit. Upgrade to a Pro account and unlock features like:",
          label: "Upgrade",
          items: items,
          checkoutLink: `/account/plans?utm_campaign=hardlimitdrops&utm_source=upload_modal&utm_medium=upgrade`,
          secondaryCtaLink: "/account/plans?utm_campaign=hardlimitdrops&utm_source=upload_modal&utm_medium=view_plans",
          anchor: hardLimitAnchor,
          heading: heading,
          proBadge: true,
        },
        nonCCTrialExpired: {
          byline: "Upgrade to continue using features like:",
          heading: "Your Pro trial has expired",
          label: "Upgrade",
          checkoutLink: `/plans?utm_campaign=trial_expiration&utm_source=web&utm_medium=${page}`,
          secondaryCtaLink: secondaryCtaLink,
          items: items,
          anchor: nonCCTrialExpiredAnchor,
          proBadge: false,
        },
        softLimit: {
          byline: "You’re close to your 50 item limit. Upgrade to a Pro account and unlock features like:",
          label: "Upgrade",
          items: items,
          checkoutLink: `/account/plans?plan=pro&trial=${this.trial}&onboarding=false&plan=pro&trial=${this.trial}&onboarding=false&utm_campaign=50dropslimithard&utm_source=45_items&utm_medium=upgrade`,
          secondaryCtaLink: "/account/plans?utm_campaign=50dropslimithard&utm_source=45_items&utm_medium=view_plans",
          anchor: softLimitAnchor,
          heading: heading,
          proBadge: true,
        },
      },
    };
  },
  methods: {
    clearRoute: function () {
      this.$router.push({ path: this.$route.path, hash: null });
    },
    trackShown: function () {
      // free upgrade prompt
      let goal = "trial";
      let campaign = "rev-12";

      if (this.promptDiscountSettings != null) {
        goal = this.promptDiscountSettings.discount + " discount";
        campaign = "rev-34";
      } else if (gon.trial_expired_prompt) {
        goal = "purchase";
        campaign = "rev-6";
      }

      track("upgrade_modal_shown", {
        campaign: campaign,
        source: this.page,
        goal: goal,
      });
    },
    hasSeenModal: function() {
      const storageKey = 'upgrade-modal-' + this.currentUser.id;
      if (localStorage.getItem(storageKey)) {
        return true;
      } else {
        localStorage.setItem(storageKey, true)
        return false;
      }
    },
    // we need to set this on demand because it's not loaded at the moment 'content' is set 
    loadUpgradeDiscountLabels: function() {
      this.content.upgradeDiscount.label = `Claim ${this.promptDiscountSettings?.discount}% discount`;
      this.content.upgradeDiscount.heading= `Get ${this.promptDiscountSettings?.discount}% off`;
      this.content.upgradeDiscount.checkoutLink = this.promptDiscountSettings?.link;
    }
  },
  computed: {
    ...mapState({
      features: state => state.features,
      user: state => state.user
    }),
    dropLimitExceeded: function() { return this.currentUser?.attributes?.soft_drop_limit_exceeded; },
    promptDiscountSettings: function() {
      const discountSettings = this.user.attributes?.experiments?.upgrade_prompt_discount;
      if (!discountSettings || _.isEmpty(discountSettings)) return;
      return discountSettings;
    },
    layout: function () {
      return this.content[this.version];
    },
    displayModal: function() {
      return gon.trial_expired_prompt || gon.free_upgrade_prompt || this.promptDiscountSettings != null;
    },
    version: function () {
      switch (this.$route.hash) {
        case this.content.trial.anchor:
          return "trial";
        case this.content.upgrade.anchor:
          return "upgrade";
        case this.content.upgradeDiscount.anchor:
          return "upgradeDiscount";
        case this.content.branding.anchor:
          return "branding";
        case this.content.hardLimit.anchor:
          return "hardLimit";
        case this.content.nonCCTrialExpired.anchor:
          return "nonCCTrialExpired";
        case this.content.softLimit.anchor:
          return "softLimit";
        default:
          return "trial";
      }
    },
    currentModal: function() {
      if (this.promptDiscountSettings != null) {
        return this.content.upgradeDiscount;
      } else if (gon.trial_expired_prompt) {
        return this.content.nonCCTrialExpired;
      } else if (this.dropLimitExceeded && !this.hasSeenModal()) {
        return this.content.softLimit;
      } else {
        return this.content.trial;
      }
    },
  },
  watch: {
    user: function () {
      // trial is not ready when the component is mounted
      // need to wait for it to become available
      if (this.loaded) return;
      this.loaded = true;

      let anchor = this.currentModal.anchor;

      if (this.displayModal && this.$route.hash !== anchor) {
        this.$router.push({ path: this.$route.path, hash: anchor });
        this.trackShown();
      }
    },
    // when LD data loads use the information to set labels in upgradeDiscount modal
    promptDiscountSettings: function() { this.loadUpgradeDiscountLabels() }
  }
};
</script>

<!--
  Not scoped because we cannot pass in scoped classes to vue-bootstrap
  from grandparent components when they are passed in via class directives.
  ex: content-class, modal-class, etc.
-->
<style lang="scss">
@import "@/packs/application.scss";

html .free-upgrade-modal {
  font-family: SFPro;

  .modal-body {
    padding: 0;
  }

  .modal-dialog {
    top: 30%;

    @media screen and (max-width: 500px) {
      top: 15%;
    }
  }

  .modal-content {
    max-width: 32rem;
    border-radius: 16px;
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.08);
    border: solid 0.5px #b5b5b5;
    background-color: $white;
    padding: 2.313rem 3.3rem 1.688rem 3.3rem;

    .orange-text {
      font-weight: 600;
      color: #e87d2b;
    }

    @media screen and (max-width: 500px) {
      padding: 1rem;
    }
  }
}
</style>

<style lang="scss" scoped>
@import "@/packs/application.scss";

.free-upgrade-modal .modal-content {
  .masthead {
    font-size: 2rem;
    font-weight: 600;
    color: $black;

    @media screen and (max-width: 500px) {
      font-size: 1.5rem;
    }
  }

  .pro-badge {
    color: white;
    text-align: center;
    font-weight: 900;
    margin-left: 10px;
    font-size: 1.5rem;
    width: 4.938rem;
    height: 2.375rem;
    border-radius: 288px;
    border: solid white 2px;
    box-shadow: 0 2px 7px 2px rgba(0, 0, 0, 0.21);
    background-color: $white;
  }

  .badges-pro-shadow {
    max-width: 6.063rem;
    max-height: 3.5rem;
    object-fit: contain;
  }

  .byline {
    font-size: 1rem;
    color: #333333;
    margin: 0.438rem 0 2.063rem 0;
    justify-content: center;
  }

  .illustration {
    object-fit: contain;
  }

  .item-text {
    max-width: 6.375rem;
    max-height: 2.875rem;
    margin: 1rem 0 0;
    font-size: 1.25rem;
    font-weight: 900;
    text-align: center;
    color: #333333;

    @media screen and (max-width: 500px) {
      font-size: 1rem;
    }
  }

  .button-frame {
    max-width: 25.125rem;
    max-height: 3rem;
    margin: 3.313rem 0 1.25rem;
    border-radius: 5px;
    background-image: linear-gradient(96deg, #f09819 14%, #ff5858 100%);

    .label {
      font-size: 1rem;
      text-align: center;
      color: $white;
    }
  }

  .pricing-link {
    font-size: 1rem;
    text-align: center;
    color: $bright-blue;
    text-decoration: none;
  }
}
</style>
